import React, { useState, useEffect } from "react";
import { MantineSize, TextInput, Tooltip } from "@mantine/core";
import { AmountType } from "../../types/commission";

interface CommissionInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onError?: (error: string | null) => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  /** Input size (defaults to Mantine's "sm"); "xs" fits a table cell. */
  size?: MantineSize;
  /** Accessible name when there is no visible label (e.g. in a table row). */
  ariaLabel?: string;
}

/**
 * Parse a formatted commission display value (e.g., "15%", "$25") back into
 * numeric amount + AmountType for the API. Returns null for empty/invalid input.
 */
export const parseCommissionValue = (
  displayValue: string,
): { amount: number; amountType: AmountType } | null => {
  if (!displayValue || !displayValue.trim()) return null;
  const trimmed = displayValue.trim();

  // Percentage format
  if (trimmed.endsWith("%")) {
    const num = parseFloat(trimmed.slice(0, -1));
    if (!isNaN(num) && num >= 0 && num <= 100) {
      return { amount: num, amountType: "Percentage" };
    }
    return null;
  }

  // Dollar format
  if (trimmed.startsWith("$")) {
    const num = parseFloat(trimmed.slice(1));
    if (!isNaN(num) && num >= 0) {
      return { amount: num, amountType: "Fixed Amount" };
    }
    return null;
  }

  return null;
};

/** Strips everything but digits, ".", "$" and "%" from typed input. */
export const keepRateCharacters = (input: string): string =>
  input.replace(/[^0-9.$%]/g, "");

const CommissionInput: React.FC<CommissionInputProps> = ({
  value,
  onChange,
  onBlur,
  onError,
  disabled = false,
  error,
  label,
  placeholder = "e.g. 15% or $25",
  size,
  ariaLabel,
}) => {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    if (value === "Not Set Yet") {
      setDisplayValue("");
    } else {
      setDisplayValue(value);
    }
  }, [value]);

  const validateAndFormat = (
    inputValue: string,
  ): { isValid: boolean; formattedValue: string; error?: string } => {
    if (!inputValue.trim()) {
      return { isValid: true, formattedValue: "", error: undefined };
    }

    // Check if it's a percentage format (ends with %)
    if (inputValue.includes("%")) {
      const percentMatch = inputValue.match(/^(\d+(?:\.\d{1,2})?)%$/);
      if (percentMatch) {
        const numValue = parseFloat(percentMatch[1]);
        if (numValue >= 0 && numValue <= 100) {
          return {
            isValid: true,
            formattedValue: `${numValue}%`,
            error: undefined,
          };
        } else {
          return {
            isValid: false,
            formattedValue: inputValue,
            error: "Percentage must be between 0 and 100",
          };
        }
      } else {
        return {
          isValid: false,
          formattedValue: inputValue,
          error: "Please enter a valid value (e.g. 15% or $25)",
        };
      }
    }

    // Check if it's a dollar format (starts with $)
    if (inputValue.includes("$")) {
      const dollarMatch = inputValue.match(/^\$(\d+(?:\.\d{1,2})?)$/);
      if (dollarMatch) {
        const numValue = parseFloat(dollarMatch[1]);
        if (numValue >= 0) {
          const formatted =
            numValue % 1 === 0 ? `$${numValue}` : `$${numValue.toFixed(2)}`;
          return {
            isValid: true,
            formattedValue: formatted,
            error: undefined,
          };
        } else {
          return {
            isValid: false,
            formattedValue: inputValue,
            error: "Invalid value",
          };
        }
      } else {
        return {
          isValid: false,
          formattedValue: inputValue,
          error: "Invalid value",
        };
      }
    }

    // If no $ or %, treat as percentage and add % suffix
    const numMatch = inputValue.match(/^(\d+(?:\.\d{1,2})?)$/);
    if (numMatch) {
      const numValue = parseFloat(numMatch[1]);
      if (numValue >= 0 && numValue <= 100) {
        return {
          isValid: true,
          formattedValue: `${numValue}%`,
          error: undefined,
        };
      } else {
        return {
          isValid: false,
          formattedValue: inputValue,
          error: "Percentage must be between 0 and 100",
        };
      }
    }

    return {
      isValid: false,
      formattedValue: inputValue,
      error: "Please enter a valid value (e.g. 15% or $25)",
    };
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Only what a rate can contain: digits, a decimal point, "$" and "%".
    // Letters and anything else are dropped as they're typed or pasted.
    const newValue = keepRateCharacters(event.target.value);
    setDisplayValue(newValue);
    onChange(newValue);
  };

  const handleBlur = () => {
    const validation = validateAndFormat(displayValue);

    if (validation.isValid) {
      setDisplayValue(validation.formattedValue);
      onChange(validation.formattedValue);
      if (onError) {
        onError(null);
      }
    } else {
      setDisplayValue(validation.formattedValue);
      if (onError) {
        onError(
          validation.error || "Please enter a valid value (e.g. 15% or $25)",
        );
      }
    }

    if (onBlur) {
      onBlur();
    }
  };

  const tooltipText = "Enter amount (e.g. 15% or $25.00)";

  return (
    <Tooltip label={tooltipText} position="top" withArrow>
      <TextInput
        label={label}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        size={size}
        aria-label={ariaLabel}
        styles={{
          input: { textAlign: "left" },
        }}
      />
    </Tooltip>
  );
};

export default CommissionInput;
